// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import Icon, { ICONS } from '../icons';
import { useLocalization, type Language } from '../../../config/localization';

interface DropdownOption {
  id: string | number;
  label: string;
  subtitle?: string;
  count?: number;
  icon?: string;
}

interface HybridDropdownProps {
  options: DropdownOption[];
  selectedIds: (string | number)[];
  onSelectionChange: (selectedIds: (string | number)[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  selectedLabel?: string; // Кастомный заголовок для выбранных элементов
  loading?: boolean;
  allowMultiple?: boolean;
  language: Language;
  className?: string;
}

const HybridDropdown: React.FC<HybridDropdownProps> = ({
  options,
  selectedIds,
  onSelectionChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  selectedLabel,
  loading = false,
  allowMultiple = true,
  language,
  className = ''
}) => {
  const t = useLocalization(language);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (option.subtitle || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected options for display
  const selectedOptions = options.filter(option => selectedIds.includes(option.id));


  // Handle search input focus/click to open dropdown
  const handleSearchFocus = () => {
    setIsDropdownOpen(true);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  // Handle option toggle
  const handleOptionToggle = (optionId: string | number) => {
    if (allowMultiple) {
      const isSelected = selectedIds.includes(optionId);
      if (isSelected) {
        onSelectionChange(selectedIds.filter(id => id !== optionId));
      } else {
        onSelectionChange([...selectedIds, optionId]);
      }
    } else {
      // Single selection mode - close dropdown after selection
      const newSelection = selectedIds.includes(optionId) ? [] : [optionId];
      onSelectionChange(newSelection);
      setIsDropdownOpen(false);
      setSearchTerm('');
    }
  };

  // Remove option from selection
  const handleRemoveOption = (optionId: string | number) => {
    onSelectionChange(selectedIds.filter(id => id !== optionId));
  };

  try {
    return (
      <div className={`space-y-3 ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onClick={handleSearchFocus}
          className="w-full px-3 py-2 pr-16 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2 text-gray-400">
          <Icon 
            name={ICONS.SEARCH} 
            size={16} 
            className="pointer-events-none" 
          />
          <Icon 
            name={isDropdownOpen ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN} 
            size={16} 
            className="transition-transform" 
          />
        </div>
      </div>

      {/* Selected Options Display (when dropdown is closed) */}
      {!isDropdownOpen && selectedOptions.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-400 font-medium">
            {selectedLabel || (allowMultiple 
              ? (t.widgets?.selectedCollections || 'Выбранные коллекции')
              : (t.widgets?.selected || 'Выбрано')
            )}
          </div>
          <div className="space-y-1">
            {selectedOptions.map((option) => (
              <div
                key={option.id}
                className="flex items-center justify-between p-2 bg-purple-900/30 border border-purple-500/50 rounded-lg"
              >
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  {option.icon && (
                    <Icon name={option.icon} size={14} className="text-purple-400 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium text-white truncate">
                    {option.label}
                  </span>
                  {option.count !== undefined && (
                    <span className="text-xs text-gray-400">
                      ({option.count})
                    </span>
                  )}
                </div>
                {allowMultiple && (
                  <button
                    onClick={() => handleRemoveOption(option.id)}
                    className="ml-2 p-1 rounded text-gray-400 hover:text-red-400 hover:bg-red-900/30 transition-colors"
                    title={t.widgets?.removeFromSelection || 'Убрать из выбора'}
                  >
                    <Icon name={ICONS.X} size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Options Dropdown (when open) */}
      {isDropdownOpen && (
        <div className="relative">
          {loading ? (
            <div className="text-center py-4 bg-gray-800 border border-gray-600 rounded-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent mx-auto mb-2"></div>
              <p className="text-xs text-gray-400">
                {t.widgets?.loadingCollections || 'Загрузка...'}
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg p-2">
              {filteredOptions.map((option) => {
                const isSelected = selectedIds.includes(option.id);
                
                return (
                  <label
                    key={option.id}
                    className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected 
                        ? 'bg-purple-900/30 border border-purple-500/50' 
                        : 'bg-gray-700/50 border border-transparent hover:bg-gray-600/50'
                    }`}
                  >
                    <input
                      type={allowMultiple ? "checkbox" : "radio"}
                      checked={isSelected}
                      onChange={() => handleOptionToggle(option.id)}
                      className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        {option.icon && (
                          <Icon name={option.icon} size={14} className="text-purple-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-white truncate">
                          {option.label}
                        </span>
                        {option.count !== undefined && (
                          <span className="text-xs text-gray-400">
                            ({option.count})
                          </span>
                        )}
                      </div>
                      {option.subtitle && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {option.subtitle}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
              
              {filteredOptions.length === 0 && searchTerm && (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400">
                    {emptyMessage}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      </div>
    );
  } catch (error) {
    console.error('❌ HybridDropdown error:', error);
    return <div className="text-red-500 p-2">HybridDropdown Error: {String(error)}</div>;
  }
};

export default HybridDropdown;